import React, { useEffect, useState } from 'react';
import { X, Video, ExternalLink, Save } from 'lucide-react';
interface JoinLessonPanelProps {
  isOpen: boolean;
  onClose: () => void;
  lesson: {
    id: string;
    studentName: string;
    meetingLink?: string;
  } | null;
  onSaveMeetingLink: (lessonId: string, meetingLink: string) => Promise<void>;
  darkMode: boolean;
}
export const JoinLessonPanel: React.FC<JoinLessonPanelProps> = ({
  isOpen,
  onClose,
  lesson,
  onSaveMeetingLink,
  darkMode
}) => {
  const [meetingLink, setMeetingLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Reset state when lesson changes
  useEffect(() => {
    if (lesson) {
      setMeetingLink(lesson.meetingLink || '');
      setHasUnsavedChanges(false);
    }
  }, [lesson]);
  const handleSave = async () => {
    if (!lesson || !meetingLink.trim()) return;
    setIsSaving(true);
    try {
      await onSaveMeetingLink(lesson.id, meetingLink.trim());
      // CRITICAL FIX: After successful save, mark as no unsaved changes
      // This keeps the saved link in the input field and enables "Open Meeting"
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving meeting link:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleOpenMeeting = () => {
    if (meetingLink.trim()) {
      // Add https:// if no protocol specified
      let url = meetingLink.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  const handleLinkChange = (value: string) => {
    setMeetingLink(value);
    setHasUnsavedChanges(value.trim() !== (lesson?.meetingLink || ''));
  };
  if (!isOpen || !lesson) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-2177" />

      {/* Slide-over Panel */}
      <div className={`fixed inset-y-0 right-0 w-full sm:w-[480px] z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-2178">
        <div className={`h-full flex flex-col rounded-l-2xl ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-2xl`} data-id="element-2179">
          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-2180">
            <div className="flex items-center gap-3" data-id="element-2181">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-2182">
                <Video className={`h-5 w-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} data-id="element-2183" />
              </div>
              <div data-id="element-2184">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-2185">
                  Online Lesson
                </h2>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2186">
                  {lesson.studentName}
                </p>
              </div>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'}`} aria-label="Close panel" data-id="element-2187">
              <X className="h-5 w-5" data-id="element-2188" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-2189">
            <div className="space-y-6" data-id="element-2190">
              {/* Meeting Link Section */}
              <div data-id="element-2191">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2192">
                  Meeting Link
                </label>
                <input type="text" value={meetingLink} onChange={e => handleLinkChange(e.target.value)} placeholder="Paste Zoom, Google Meet, or any meeting link" className={`w-full px-4 py-3 rounded-lg border transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500 focus:border-green-500 focus:ring-1 focus:ring-green-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-green-500 focus:ring-1 focus:ring-green-500'} focus:outline-none`} data-id="element-2193" />
                <p className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-2194">
                  This link will be saved and reused for future lessons
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3" data-id="element-2195">
                {/* Save Button (only show if there are unsaved changes) */}
                {hasUnsavedChanges && <button onClick={handleSave} disabled={isSaving || !meetingLink.trim()} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${darkMode ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-navy-700 disabled:text-gray-500' : 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-100 disabled:text-gray-400'} disabled:cursor-not-allowed`} data-id="element-2196">
                    <Save className="h-4 w-4" data-id="element-2197" />
                    {isSaving ? 'Saving...' : 'Save Meeting Link'}
                  </button>}

                {/* Open Meeting Button - Unified Green Outline Style (Both Modes) */}
                <button onClick={handleOpenMeeting} disabled={!meetingLink.trim()} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors !bg-transparent !border-2 !border-green-500 !text-green-500 hover:!bg-green-500/10 disabled:!border-gray-400 disabled:!text-gray-400 disabled:cursor-not-allowed" data-id="element-2198">
                  <ExternalLink className="h-4 w-4 !text-green-500" data-id="element-2199" />
                  Open Meeting
                </button>
              </div>

              {/* Info Box */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-700/50' : 'bg-gray-50'}`} data-id="element-2200">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2201">
                  <strong className={darkMode ? 'text-gray-300' : 'text-gray-700'} data-id="element-2202">
                    Tip:
                  </strong>{' '}
                  The meeting will open in a new tab. You can return to this
                  dashboard anytime.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};